import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import styles from "./hipaaDetail.module.scss";
import { heroInfo, aiStrategy, scorecard, auditLedger } from "./hipaaData";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const downloadPDF = async (name) => {
  const element = document.getElementById("report");

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
  });

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF("p", "mm", "a4");

  const marginX = 10; // left/right space
  const marginY = 10; // top/bottom space

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const contentWidth = pageWidth - marginX * 2;
  const contentHeight = (canvas.height * contentWidth) / canvas.width;

  pdf.addImage(imgData, "PNG", marginX, marginY, contentWidth, contentHeight);

  pdf.save(`${name}.pdf`);
};

const StatusPill = ({ status }) => {
  const map = {
    Pass: styles.pass,
    Failed: styles.fail,
    WARNING: styles.warn,
  };
  return (
    <span className={`${styles.statusPill} ${map[status]}`}>{status}</span>
  );
};

const EvidencePill = ({ evidence }) => {
  const isMissing = evidence.toLowerCase().includes("missing");
  const isEncryption = evidence.toLowerCase().includes("encryption");
  const isAnchored = evidence.toLowerCase().includes("anchored");
  const tone = isMissing
    ? styles.evidenceMissing
    : isEncryption
    ? styles.evidenceAlert
    : isAnchored
    ? styles.evidenceOk
    : styles.evidenceNeutral;
  return <span className={`${styles.evidencePill} ${tone}`}>{evidence}</span>;
};

const AuditStatus = ({ status }) => {
  const tone = status === "Verified" ? styles.auditVerified : styles.auditSync;
  return <span className={`${styles.auditStatus} ${tone}`}>{status}</span>;
};

const HipaaDetail = () => {
  const [dateQuery, setDateQuery] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const filteredAuditLedger = React.useMemo(() => {
    if (!dateQuery) {
      return auditLedger;
    }

    const normalizedQuery = dateQuery.trim().toLowerCase();
    return auditLedger.filter((row) => {
      const timestamp = [row.date, row.time, row.timestamp]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return timestamp.includes(normalizedQuery);
    });
  }, [dateQuery]);

  const pagedAuditLedger = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAuditLedger.slice(start, start + rowsPerPage);
  }, [filteredAuditLedger, page, rowsPerPage]);

  const handleDateChange = (event) => {
    setDateQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard ", href: "/" },
            { label: "Compliance" },
            { label: "HIPAA Regulatory Framework" },
          ]}
        />{" "}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportButton}
          onClick={() => downloadPDF("Hipaa")}
        >
          Export Report
        </Button>
      </div>
      <Stack id="report" direction={"column"} gap={2}>
        <Card className={styles.heroCard}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box>
              <Avatar variant="rounded" sx={{ bgcolor: "blue" }}>
                <ShieldOutlinedIcon />
              </Avatar>
            </Box>
            <div>
              <Box
                // className={styles.titleBlock}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  // alignItems: "flex-start",
                  flex: 1,
                }}
              >
                <div className={styles.heroTitle}>{heroInfo.title}</div>
                <Chip
                  label={heroInfo.badge}
                  size="small"
                  className={styles.badge}
                />
              </Box>
              <div className={styles.heroSubtitle}>{heroInfo.subtitle}</div>
            </div>{" "}
          </Box>
          <div className={styles.heroRight}>
            <div className={styles.scoreLabel}>Overall Compliance Score</div>
            <div className={styles.scoreValue}>{heroInfo.score}%</div>
          </div>
        </Card>

        <Card
          className={styles.aiCard}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Box>
            <Avatar variant="rounded" sx={{ bgcolor: "blue" }}>
              <FlashOnOutlinedIcon />
            </Avatar>
          </Box>
          <Box>
            <Typography fontWeight={700}>AI Remediation Strategy</Typography>
            <div className={styles.aiText}>{aiStrategy}</div>
          </Box>
        </Card>

        <Card className={styles.tableCard}>
          <Typography className={styles.sectionTitle} fontWeight={600}>
            Compliance Scorecard
          </Typography>
          <TableContainer className={styles.tableContainer}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell className={styles.tableHead}>Control ID</TableCell>
                  <TableCell className={styles.tableHead}>
                    Requirement Name
                  </TableCell>
                  <TableCell className={styles.tableHead}>Status</TableCell>
                  <TableCell className={styles.tableHead}>
                    Evidence Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scorecard.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <StatusPill status={row.status} />
                    </TableCell>
                    <TableCell>
                      <EvidencePill evidence={row.evidence} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <Typography className={styles.sectionTitle} fontWeight={600}>
              Audit Ledger
            </Typography>
            <TextField
              label="Search by Date"
              type="date"
              size="small"
              value={dateQuery}
              onChange={handleDateChange}
              className={styles.dateSearch}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <TableContainer className={styles.tableContainer}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell className={styles.tableHead}>Event ID</TableCell>
                  <TableCell className={styles.tableHead}>Timestamp</TableCell>
                  <TableCell className={styles.tableHead}>
                    Action Performed
                  </TableCell>
                  <TableCell className={styles.tableHead}>
                    Actor / User
                  </TableCell>
                  <TableCell className={styles.tableHead}>
                    Blockchain Hash (ID)
                  </TableCell>
                  <TableCell className={styles.tableHead}>Nodes</TableCell>
                  <TableCell className={styles.tableHead}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedAuditLedger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className={styles.emptyState}>
                      No audit events match the selected date.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedAuditLedger.map((row) => (
                    <TableRow key={row.event}>
                      <TableCell>{row.event}</TableCell>
                      <TableCell>
                        {row.date ? `${row.date} ${row.time}` : row.time}
                      </TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>{row.actor}</TableCell>
                      <TableCell>{row.hash}</TableCell>
                      <TableCell>{row.nodes}</TableCell>
                      <TableCell>
                        <AuditStatus status={row.status} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredAuditLedger.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            className={styles.tablePagination}
          />
        </Card>
      </Stack>
    </Box>
  );
};

export default HipaaDetail;
